import { Component, Optional, Inject, OnDestroy } from '@angular/core';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { catchError, of, Subscription, tap } from 'rxjs';
import { ClassroomManagementService } from '@edtech-ui/core/services/classroom-management-service/classroom-management.service';
import { MatIconModule } from '@angular/material/icon';
import { JoinClassPayload } from '@edtech-ui/core/models/members/join-class-code';
import { Roles } from '@edtech-ui/constants/roles.enum';
import { CommonModule } from '@angular/common';
import { AuthServiceService } from '@edtech-ui/core/services/authService/auth-service.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

@Component({
	selector: 'app-classroom-post-dialog-box',
	standalone: true,
	imports: [MatDialogModule, ReactiveFormsModule, MatIconModule, CommonModule, MatProgressSpinnerModule],
	templateUrl: './classroom-post-dialog-box.component.html',
	styleUrl: './classroom-post-dialog-box.component.scss'
})
export class ClassroomPostDialogBoxComponent implements OnDestroy {
	joinClassCodeForm: FormGroup;
	subscription = new Subscription();
	classroomId: number = 0;
	Roles = Roles;
	userRole: Roles;
	isLoading:boolean = false;

	constructor(
		@Optional() private dialogRef: MatDialogRef<ClassroomPostDialogBoxComponent>,
		@Inject(MAT_DIALOG_DATA) public data: { userRole: Roles },
		private router: Router,
		private _classroomService: ClassroomManagementService,
		private authService: AuthServiceService,
		private _toastr: ToastrService
	) {
		this.userRole = data.userRole || Roles.TEACHER;
		this.joinClassCodeForm = new FormGroup({
			class_code: new FormControl('', [
				Validators.required,
				Validators.pattern(/^[A-Z0-9]{8}$/)
			])
		});
	}

	joinClassroom() {
		if (this.joinClassCodeForm.valid) {
			const userId = this.authService.getUserId();
			const token = this.authService.getToken();
			this.isLoading = true;
			if (!userId || !token) {
				this._toastr.error('Session expired. Please log in again.');
				this.router.navigate(['']);
				this.closePostDialog();
				return;
			}

			if (this.userRole !== Roles.TEACHER && this.userRole !== Roles.STUDENT) {
				this._toastr.error('Invalid user role. Please contact support.');
				this.router.navigate(['']);
				this.closePostDialog();
				return;
			}

			const payload: JoinClassPayload = {
				class_code: this.joinClassCodeForm.value.class_code,
				user_id: userId
			};

			this.subscription.add(
				this._classroomService.postJoinClass(payload).pipe(
					tap(response => {
						this.isLoading = false;
						if (response.Success) {
							this.classroomId = response.Data.class_id;
							this._toastr.success(response.Message || 'You have joined the Class Successfully');
							this.closePostDialog();
							const route = this.userRole === Roles.TEACHER
								? `/teacher-dashboard/classroom-detail-page/${this.classroomId}/member`
								: `/student-dashboard/classroom-detail-page/${this.classroomId}/members`;
							this.router.navigate([route]);
						} else {
							this._toastr.error(response.Message || 'Failed to join the class.');
						}
					}),
					catchError ( err =>{
						this.isLoading = false;
						this._toastr.error('Something went wrong while joining the class');
						return of(null);
					})
				).subscribe()
			);
		}
	}

	closePostDialog(): void {
		this.dialogRef?.close();
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}
}